import { useState } from "react";
import { Link, useParams } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Trophy,
  Users,
  Pencil,
  Target,
  TrendingUp,
  TrendingDown,
  Banknote,
  Calendar,
  Percent,
  MapPin,
  Clock,
  CheckCircle2,
  ChevronRight,
  Briefcase,
} from "lucide-react";
import SellerProfileSheet from "@/components/Crm/SellerProfileSheet";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type PeriodKind = "month" | "quarter" | "year";

function CrmSellerDetailBody({ userId }: { userId: number }) {
  const { can } = usePermissions();
  const canManage = can("crm:sellers_manage");
  const canViewCommissions = can("crm:commissions_view_all") || can("crm:commissions_view");
  const [kind, setKind] = useState<PeriodKind>("month");
  const [editOpen, setEditOpen] = useState(false);

  const query = trpc.crm.sellers.get.useQuery({ userId, period: { kind } });

  // Lista de usuários para obter nome (profile guarda apenas userId; a query get já traz).
  // Busca comissões do vendedor (se permitido).
  const commissionsQuery = trpc.crm.commissions.list.useQuery(
    canViewCommissions ? { userId, limit: 50 } : (undefined as any),
    { enabled: canViewCommissions },
  );

  const commissionsSummary = trpc.crm.commissions.summary.useQuery(
    canViewCommissions ? { userId } : (undefined as any),
    { enabled: canViewCommissions },
  );

  // Necessário para exibir nome/jobTitle do usuário — deve ficar antes de qualquer return.
  const sellersListQuery = trpc.crm.sellers.list.useQuery({});

  if (query.isLoading) {
    return (
      <div className="space-y-4 max-w-6xl">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-40" />
        <Skeleton className="h-48" />
      </div>
    );
  }
  if (!query.data) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <p className="font-semibold">Vendedor não encontrado.</p>
          <Link
            href="/crm/sellers"
            className="text-sm text-primary hover:underline mt-2 inline-block"
          >
            Voltar ao ranking
          </Link>
        </CardContent>
      </Card>
    );
  }

  const { profile, stats, wonDeals } = query.data;
  const sellerRow = (sellersListQuery.data ?? []).find(
    (r: any) => r.profile.userId === userId,
  );
  const user = sellerRow?.user;
  const monthlyGoal = Number(profile.monthlyGoal ?? 0);
  const achievement = stats.goalAchievement;
  const territories = (profile.territories as string[]) ?? [];

  return (
    <div className="space-y-5 max-w-6xl">
      {/* ── Breadcrumb ──────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground overflow-x-auto -mx-1 px-1 whitespace-nowrap">
        <Link
          href="/crm/sellers"
          className="hover:text-foreground flex items-center gap-1 shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          Vendedores
        </Link>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="truncate max-w-[220px]">{user?.name ?? "Vendedor"}</span>
      </div>

      {/* ── Hero ────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Avatar className="h-16 w-16 shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
                {initials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {profile.isActive ? (
                  <Badge className="bg-emerald-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Ativo
                  </Badge>
                ) : (
                  <Badge variant="secondary">Inativo</Badge>
                )}
                {profile.joinedAt && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Desde {new Date(profile.joinedAt).toLocaleDateString("pt-BR")}
                  </span>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">
                {user?.name ?? user?.email ?? "Vendedor"}
              </h1>
              {user?.jobTitle && (
                <p className="text-sm text-muted-foreground">{user.jobTitle}</p>
              )}
              {territories.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  {territories.map((uf) => (
                    <Badge key={uf} variant="outline" className="text-[10px]">
                      {uf}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            {canManage && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Editar perfil</span>
              </Button>
            )}
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Meta mensal
              </div>
              <div className="text-lg font-bold tabular-nums flex items-center gap-1.5">
                <Target className="h-4 w-4 text-muted-foreground" />
                {formatBRL(monthlyGoal)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Comissão
              </div>
              <div className="text-lg font-bold tabular-nums flex items-center gap-1.5">
                <Percent className="h-4 w-4 text-muted-foreground" />
                {Number(profile.commissionPct ?? 0).toFixed(2)}%
              </div>
            </div>
            <div className="col-span-2 md:col-span-1">
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Territórios
              </div>
              <div className="text-sm">
                {territories.length === 0 ? (
                  <span className="text-muted-foreground">Nacional</span>
                ) : (
                  territories.join(", ")
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Period picker ─────────────────────── */}
      <div className="inline-flex items-center gap-1 p-1 bg-muted/60 rounded-lg">
        {(["month", "quarter", "year"] as PeriodKind[]).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition ${
              kind === k
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {k === "month" ? "Mês" : k === "quarter" ? "Trimestre" : "Ano"}
          </button>
        ))}
      </div>

      {/* ── Goal progress ──────────────────────── */}
      {monthlyGoal > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Meta vs. realizado</span>
              <span className="text-sm font-bold tabular-nums">
                {formatBRL(stats.wonRevenue)} / {formatBRL(monthlyGoal)}
              </span>
            </div>
            <Progress
              value={Math.min(100, achievement ?? 0)}
              className={
                achievement !== null && achievement >= 100
                  ? "[&>div]:bg-emerald-500"
                  : ""
              }
            />
            {achievement !== null && (
              <p
                className={`text-xs ${
                  achievement >= 100
                    ? "text-emerald-600 font-semibold"
                    : achievement >= 70
                    ? "text-amber-600"
                    : "text-muted-foreground"
                }`}
              >
                {achievement >= 100
                  ? `🎉 Meta batida! ${achievement.toFixed(0)}% do objetivo.`
                  : `${achievement.toFixed(0)}% da meta atingida.`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Stats ──────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Receita"
          value={formatCompact(stats.wonRevenue)}
          sub={`${stats.wonCount} ganhos`}
          icon={TrendingUp}
          tint="emerald"
        />
        <MetricCard
          label="Perdidos"
          value={String(stats.lostCount)}
          sub="no período"
          icon={TrendingDown}
          tint={stats.lostCount > 0 ? "red" : "default"}
        />
        <MetricCard
          label="Ticket médio"
          value={formatCompact(stats.avgTicket)}
          sub={stats.wonCount > 0 ? "por venda" : "—"}
          icon={Banknote}
        />
        <MetricCard
          label="Ciclo médio"
          value={stats.avgCycleDays === null ? "—" : `${stats.avgCycleDays}d`}
          sub={
            stats.winRate !== null ? `${stats.winRate.toFixed(0)}% win rate` : "—"
          }
          icon={Clock}
        />
      </div>

      {/* ── Deals ganhos recentes + Comissões ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-emerald-600" />
              Deals ganhos
              {wonDeals.length > 0 && (
                <Badge variant="secondary">{wonDeals.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {wonDeals.length === 0 ? (
              <div className="text-center py-6">
                <Trophy className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-40" />
                <p className="text-sm text-muted-foreground">
                  Nenhum deal ganho no período.
                </p>
              </div>
            ) : (
              wonDeals.map((d: any) => (
                <Link
                  key={d.id}
                  href={`/crm/deals/${d.id}`}
                  className="block rounded-md border p-2.5 hover:bg-muted/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{d.title}</span>
                    <span className="text-sm font-semibold tabular-nums">
                      {formatBRL(d.value)}
                    </span>
                  </div>
                  {d.closedAt && (
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Calendar className="h-3 w-3" />
                      {new Date(d.closedAt).toLocaleDateString("pt-BR")}
                    </div>
                  )}
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {canViewCommissions && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Percent className="h-4 w-4" />
                Comissões
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-2">
                  <div className="text-[10px] uppercase font-semibold text-amber-700 dark:text-amber-300">
                    Pendentes
                  </div>
                  <div className="font-bold tabular-nums mt-0.5">
                    {formatCompact(commissionsSummary.data?.pending ?? 0)}
                  </div>
                </div>
                <div className="rounded-md bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 p-2">
                  <div className="text-[10px] uppercase font-semibold text-sky-700 dark:text-sky-300">
                    Aprovadas
                  </div>
                  <div className="font-bold tabular-nums mt-0.5">
                    {formatCompact(commissionsSummary.data?.approved ?? 0)}
                  </div>
                </div>
                <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-2">
                  <div className="text-[10px] uppercase font-semibold text-emerald-700 dark:text-emerald-300">
                    Pagas
                  </div>
                  <div className="font-bold tabular-nums mt-0.5">
                    {formatCompact(commissionsSummary.data?.paid ?? 0)}
                  </div>
                </div>
              </div>

              {(commissionsQuery.data ?? []).length === 0 ? (
                <div className="text-center py-6">
                  <Percent className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-40" />
                  <p className="text-sm text-muted-foreground">
                    Nenhuma comissão ainda.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(commissionsQuery.data ?? []).slice(0, 6).map((row: any) => (
                    <div
                      key={row.commission.id}
                      className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <CommissionStatusDot status={row.commission.status} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {row.deal?.title ?? `Deal #${row.commission.dealId}`}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {row.commission.referenceMonth}/
                            {row.commission.referenceYear} ·{" "}
                            {Number(row.commission.pct).toFixed(2)}%
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-bold tabular-nums">
                        {formatBRL(row.commission.amount)}
                      </div>
                    </div>
                  ))}
                  {(commissionsQuery.data ?? []).length > 6 && (
                    <Link
                      href={`/crm/commissions?userId=${userId}`}
                      className="block text-center text-xs text-primary hover:underline pt-2"
                    >
                      Ver todas ({(commissionsQuery.data ?? []).length})
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <SellerProfileSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        userId={userId}
      />
    </div>
  );
}

function CommissionStatusDot({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    pending: { label: "Pendente", color: "bg-amber-500" },
    approved: { label: "Aprovada", color: "bg-sky-500" },
    paid: { label: "Paga", color: "bg-emerald-500" },
    cancelled: { label: "Cancelada", color: "bg-slate-400" },
  };
  const cfg = map[status] ?? map.pending;
  return (
    <span
      className={`h-2 w-2 rounded-full shrink-0 ${cfg.color}`}
      title={cfg.label}
    />
  );
}

function MetricCard({
  label,
  value,
  sub,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tint?: "default" | "emerald" | "red" | "primary";
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums truncate">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tintMap[tint]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmSellerDetail() {
  const params = useParams<{ userId: string }>();
  const userId = Number(params.userId);
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:sellers_view">
        {Number.isFinite(userId) ? (
          <CrmSellerDetailBody userId={userId} />
        ) : (
          <div className="py-12 text-center">ID inválido.</div>
        )}
      </PermissionGuard>
    </DashboardLayout>
  );
}
